import { z } from 'zod';
import { zAdminComment } from './admin';
import { zLocale } from './common';

/** Presentation only. Ledger, stakes and payouts always retain their original integer units. */
export const zCurrencyProfile = z
  .object({
    enabled: z.boolean(),
    name: z
      .string()
      .trim()
      .min(1)
      .max(40)
      .regex(/^[\p{L}\p{N} ._-]+$/u),
    ticker: z
      .string()
      .trim()
      .min(2)
      .max(16)
      .regex(/^[A-Z][A-Z0-9]*(?: [A-Z0-9]+)*$/),
    icon: z.enum(['leyo', 'usdt', 'custom']),
    image: z
      .string()
      .max(90_000)
      .regex(/^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/)
      .nullable(),
  })
  .strict()
  .refine((p) => p.icon !== 'custom' || p.image !== null, {
    path: ['image'],
    message: 'custom icon needs an image',
  });
export type CurrencyProfile = z.infer<typeof zCurrencyProfile>;
export const DEFAULT_CURRENCY: CurrencyProfile = {
  enabled: false,
  name: 'Leyo Token',
  ticker: 'LEYO',
  icon: 'leyo',
  image: null,
};
export const USDT_CURRENCY: CurrencyProfile = {
  enabled: true,
  name: 'USDT',
  ticker: 'USDT',
  icon: 'usdt',
  image: null,
};
/** The same owner-selected label is used in admin previews and on the public site. */
export const USDT_CHINA_CURRENCY: CurrencyProfile = {
  ...USDT_CURRENCY,
  name: 'USDT CHINA',
  ticker: 'USDT CHINA',
};

/** Missing language entries inherit the legacy global profile. */
export const zCurrencyLocales = z
  .object({
    en: zCurrencyProfile.optional(),
    de: zCurrencyProfile.optional(),
    zh: zCurrencyProfile.optional(),
    ru: zCurrencyProfile.optional(),
  })
  .strict();
export type CurrencyLocales = z.infer<typeof zCurrencyLocales>;

export const zCurrencyState = z.object({
  revision: z.number().int().nonnegative(),
  active: zCurrencyProfile,
  draft: zCurrencyProfile,
  activeByLocale: zCurrencyLocales.optional(),
  draftByLocale: zCurrencyLocales.optional(),
});
export type CurrencyState = z.infer<typeof zCurrencyState>;
export const zPublicCurrency = z.object({
  revision: z.number().int().nonnegative(),
  active: zCurrencyProfile,
  activeByLocale: zCurrencyLocales.optional(),
});
export const zCurrencySave = z
  .object({
    revision: z.number().int().nonnegative(),
    locale: zLocale,
    profile: zCurrencyProfile,
    comment: zAdminComment,
  })
  .strict();
export const zCurrencyPublish = z
  .object({ revision: z.number().int().nonnegative(), locale: zLocale, comment: zAdminComment })
  .strict();
